
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import RibaStages from "@/components/RibaStages";
import DashboardStats from "@/components/DashboardStats";
import ProjectProgressChart from "@/components/ProjectProgressChart";
import StageActivitiesSummary from "@/components/StageActivitiesSummary";
import { useAuth } from "@/contexts/AuthContext";
import { useProject } from "@/contexts/ProjectContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  CalendarDays, Clock, FileText, Plus, ChevronRight, 
  ArrowUpRight, FileBarChart, Calendar, BarChart4, 
  FileCheck, Clock3, AlertTriangle, Building, FolderPlus
} from "lucide-react";

export default function Dashboard() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { projects, currentProject, setCurrentProject, isLoading: projectsLoading, error: projectsError } = useProject();
  const navigate = useNavigate();

  console.log('Dashboard render - authLoading:', authLoading, 'projectsLoading:', projectsLoading);
  console.log('Dashboard render - isAuthenticated:', isAuthenticated, 'projects:', projects?.length || 0);
  console.log('Dashboard render - currentProject:', currentProject?.name || 'none');
  console.log('Dashboard render - currentProject.stages:', currentProject?.stages?.length || 'undefined');

  // Set first project as current if none selected
  useEffect(() => {
    if (projects && projects.length > 0 && !currentProject) {
      console.log('Dashboard - setting first project as current:', projects[0]?.name);
      console.log('Dashboard - first project stages:', projects[0]?.stages?.length);
      setCurrentProject(projects[0]);
    }
  }, [projects, currentProject, setCurrentProject]);

  // Redirect if not authenticated (only after auth loading is complete)
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      console.log('Dashboard - redirecting to login');
      navigate("/login");
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Show loading while authentication is still loading
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-sm text-muted-foreground">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Don't render anything if not authenticated (let useEffect handle redirect)
  if (!isAuthenticated) {
    return null;
  }

  // Show loading while projects are loading
  if (projectsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading projects...</p>
        </div>
      </div>
    );
  }

  // Handle projects loading error
  if (projectsError) {
    console.error('Dashboard - projects error:', projectsError);
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-500 mb-4">Error loading projects: {projectsError.message}</div>
          <Button onClick={() => {
            // Clear localStorage and reload
            localStorage.removeItem('mock_projects');
            window.location.reload();
          }}>
            Reset and Retry
          </Button>
        </div>
      </div>
    );
  }

  // If no projects exist, redirect to project selector
  if (!projects || projects.length === 0) {
    console.log('Dashboard - no projects found, redirecting to project selector');
    navigate("/projects-selector");
    return null;
  }

  // Show loading if no project is selected but we have projects
  if (!currentProject) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-sm text-muted-foreground">Preparing project...</p>
        </div>
      </div>
    );
  }

  // Additional validation for current project structure
  if (!currentProject.stages || !Array.isArray(currentProject.stages)) {
    console.error('Dashboard - currentProject has invalid stages:', currentProject);
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-500 mb-4">Project data is corrupted</div>
          <Button onClick={() => {
            // Clear localStorage and reload
            localStorage.removeItem('mock_projects');
            window.location.reload();
          }}>
            Reset Project Data
          </Button>
        </div>
      </div>
    );
  }

  const handleStageClick = (stageId: number) => {
    console.log(`Clicked on stage: ${stageId}`);
    navigate(`/stage/${stageId}`);
  };

  // Get upcoming activities with comprehensive defensive programming
  const upcomingActivities = (currentProject.stages || [])
    .filter(stage => stage && stage.activities)
    .flatMap((stage) => (stage.activities || []))
    .filter((activity) => activity && activity.status !== "completed")
    .slice(0, 5);

  // Calculate Information Exchange status across all stages
  const calculateInfoExchangeStatus = () => {
    let complete = 0;
    let total = 0;
    
    (currentProject.stages || []).forEach(stage => {
      if (stage && stage.informationExchanges && Array.isArray(stage.informationExchanges)) {
        total += stage.informationExchanges.length;
        complete += stage.informationExchanges.filter(
          item => item && (item.status === "complete" || item.status === "validated")
        ).length;
      }
    });
    
    return {
      complete,
      total,
      percentage: total > 0 ? Math.round((complete / total) * 100) : 0
    };
  };

  const infoExchangeStatus = calculateInfoExchangeStatus();

  return (
    <div className="flex flex-col min-h-screen">
      <NavBar />
      
      <main className="flex-1 container py-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Project Dashboard</h1>
            <p className="text-gray-500">
              {currentProject.name} • Client: {currentProject.client}
            </p>
          </div>
          <div className="flex items-center gap-2 mt-4 sm:mt-0">
            <Button variant="outline" className="flex items-center" onClick={() => navigate("/projects-selector")}>
              <FileBarChart className="mr-2 h-4 w-4" /> Back to Projects
            </Button>
            <Button variant="outline" className="flex items-center" onClick={() => navigate("/reports")}>
              <FileBarChart className="mr-2 h-4 w-4" /> View Reports
            </Button>
            <Button variant="outline" className="flex items-center" onClick={() => navigate("/export")}>
              <FileText className="mr-2 h-4 w-4" /> Export Report
            </Button>
          </div>
        </div>
        
        {/* RIBA Stages */}
        <RibaStages 
          stages={currentProject.stages || []}
          currentStage={currentProject.currentStage}
          onClick={handleStageClick}
        />
        
        <Tabs defaultValue="overview" className="mt-6">
          <TabsList className="mb-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="programme">Programme</TabsTrigger>
            <TabsTrigger value="risks">Risk Management</TabsTrigger>
            <TabsTrigger value="information">Information Exchange</TabsTrigger>
            <TabsTrigger value="governance">Governance</TabsTrigger>
          </TabsList>
          
          <TabsContent value="overview">
            {/* Key Stats */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Project Overview</h2>
              <Button variant="ghost" size="sm" className="flex items-center text-blue-600">
                View Details <ArrowUpRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
            <DashboardStats project={currentProject} />
            
            {/* Activities */}
            <div className="mt-6">
              {/* Recent Activities */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle>Recent Activities</CardTitle>
                  <Button variant="ghost" size="sm" className="text-blue-600 p-0 h-auto" onClick={() => navigate("/activities")}>
                    View All <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {upcomingActivities.length > 0 ? (
                      upcomingActivities.map((activity, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-4 p-3 border rounded-md hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => navigate(`/stage/${activity.stage}`)}
                        >
                          <div>
                            {activity.status === "in-progress" ? (
                              <Clock className="h-5 w-5 text-blue-500" />
                            ) : (
                              <CalendarDays className="h-5 w-5 text-gray-500" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{activity.name}</p>
                            <p className="text-xs text-gray-500 truncate">
                              Due {activity.dueDate.toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span
                              className={`px-2 py-1 text-xs rounded-full ${
                                activity.status === "in-progress"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {activity.status === "in-progress"
                                ? "In Progress"
                                : "Not Started"}
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-500">No activities found</p>
                        <Button className="mt-4" variant="outline" size="sm" onClick={() => navigate(`/stage/${currentProject.currentStage}`)}>
                          Add Activity
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-4">
                    <Button variant="outline" className="w-full" onClick={() => navigate("/activities")}>
                      View All Activities
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          
          <TabsContent value="programme">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Programme Management</h2>
              <Button variant="outline" size="sm" className="flex items-center" onClick={() => navigate("/calendar")}>
                <Calendar className="mr-2 h-4 w-4" /> Calendar View
              </Button>
            </div>
            
            <div className="grid grid-cols-1 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center">
                    <BarChart4 className="mr-2 h-5 w-5" />
                    Programme Timeline
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                        <span className="text-sm">Planned</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="text-sm">Actual</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
                        <span className="text-sm">Forecast</span>
                      </div>
                    </div>
                  
                    <div className="space-y-4">
                      {(currentProject.stages || []).map((stage, index) => (
                        <div key={index} className="relative">
                          <div className="flex items-center mb-1">
                            <span className="text-sm font-medium w-36">Stage {stage.id}: {stage.name}</span>
                            <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-blue-500 rounded-full"
                                style={{width: `${stage.progress}%`}}
                              ></div>
                            </div>
                            <span className="ml-2 text-sm">{stage.progress}%</span>
                          </div>
                          
                          <div className="flex items-center text-xs text-muted-foreground">
                            <div className="w-36"></div>
                            <div className="flex-1 relative">
                              <div className="absolute left-0 top-0">
                                {stage.dateStarted ? stage.dateStarted.toLocaleDateString() : "Not started"}
                              </div>
                              <div className="absolute right-0 top-0">
                                {stage.dateCompleted ? stage.dateCompleted.toLocaleDateString() : "In progress"}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-6">
                      <Button variant="outline" size="sm" onClick={() => navigate("/gantt")}>
                        View Detailed Gantt Chart
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <StageActivitiesSummary 
                project={currentProject} 
                onActivityClick={(stageId, activityId) => {
                  navigate(`/stage/${stageId}`);
                }}
              />
            </div>
          </TabsContent>
          
          
          <TabsContent value="risks">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Risk Management</h2>
              <Button size="sm" onClick={() => navigate("/risks/new")}>Add New Risk</Button>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle>Risk Certainty Tracker</CardTitle>
                </CardHeader>
                <CardContent>
                  <ProjectProgressChart project={currentProject} />
                </CardContent>
              </Card>
              
              <Card className="lg:col-span-2">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle>Key Project Risks</CardTitle>
                  <Button variant="ghost" size="sm" className="text-blue-600 p-0 h-auto" onClick={() => navigate("/risks")}>
                    View All <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Risk</th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Owner</th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stage</th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Updated</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {currentProject.risks.slice(0, 5).map((risk, index) => {
                          const score = risk.probability * risk.impact;
                          let scoreClass = '';
                          if (score >= 16) scoreClass = 'bg-red-100 text-red-800';
                          else if (score >= 10) scoreClass = 'bg-orange-100 text-orange-800';
                          else if (score >= 4) scoreClass = 'bg-amber-100 text-amber-800';
                          else scoreClass = 'bg-green-100 text-green-800';
                          
                          return (
                            <tr key={risk.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/risks/${risk.id}`)}>
                              <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{risk.name}</td>
                              <td className="px-3 py-4 whitespace-nowrap text-sm">
                                <span className={`inline-flex items-center justify-center px-2 py-1 text-xs font-bold rounded ${scoreClass}`}>
                                  {score}
                                </span>
                              </td>
                              <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{risk.owner}</td>
                              <td className="px-3 py-4 whitespace-nowrap text-sm">
                                <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                                  risk.status === 'open' ? 'bg-amber-100 text-amber-800' :
                                  risk.status === 'mitigated' ? 'bg-blue-100 text-blue-800' :
                                  'bg-green-100 text-green-800'
                                }`}>
                                  {risk.status.charAt(0).toUpperCase() + risk.status.slice(1)}
                                </span>
                              </td>
                              <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">Stage {risk.stage}</td>
                              <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(risk.dateUpdated).toLocaleDateString()}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          
          <TabsContent value="information">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Information Exchange</h2>
              <div className="flex items-center gap-2">
                <div className="text-sm text-muted-foreground">
                  {infoExchangeStatus.complete} of {infoExchangeStatus.total} items complete ({infoExchangeStatus.percentage}%)
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate("/information-exchange")}>View All</Button>
              </div>
            </div>
            
            <Card>
              <CardContent className="p-0">
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {(currentProject.stages || []).map((stage) => (
                      <div 
                        key={stage.id}
                        className={`border rounded-md p-4 cursor-pointer hover:border-primary transition-colors ${
                          stage.id === currentProject.currentStage ? "border-primary bg-primary/5" : ""
                        }`}
                        onClick={() => navigate(`/stage/${stage.id}`)}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-medium">Stage {stage.id}: {stage.name}</h3>
                          <div className={`w-3 h-3 rounded-full ${
                            stage.status === "completed" ? "bg-green-500" : 
                            stage.status === "in-progress" ? "bg-blue-500" :
                            "bg-gray-300"
                          }`}></div>
                        </div>
                        
                        <div className="text-sm text-muted-foreground mb-2">
                          Information Exchange: {
                            stage.informationExchanges ? 
                              `${stage.informationExchanges.filter(i => i.status === "complete" || i.status === "validated").length}/${stage.informationExchanges.length} Items` :
                              "Not configured"
                          }
                        </div>
                        
                        <div className="flex flex-wrap gap-2 mt-3">
                          {stage.informationExchanges?.slice(0, 3).map(exchange => (
                            <div 
                              key={exchange.id}
                              className={`px-2 py-1 text-xs rounded-full flex items-center ${
                                exchange.status === 'validated' ? 'bg-green-100 text-green-800' :
                                exchange.status === 'complete' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {exchange.status === 'validated' ? 
                                <FileCheck className="h-3 w-3 mr-1" /> : 
                                exchange.status === 'complete' ? 
                                <FileCheck className="h-3 w-3 mr-1" /> : 
                                <Clock3 className="h-3 w-3 mr-1" />
                              }
                              <span className="truncate max-w-[120px]">{exchange.name}</span>
                            </div>
                          ))}
                          {stage.informationExchanges && stage.informationExchanges.length > 3 && (
                            <div className="px-2 py-1 text-xs bg-gray-100 rounded-full">
                              +{stage.informationExchanges.length - 3} more
                            </div>
                          )}
                        </div>
                        
                        <div className="flex items-center text-xs mt-3">
                          <ChevronRight className="h-4 w-4 text-muted-foreground mr-1" /> 
                          View Details
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          
          <TabsContent value="governance">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Governance & Compliance</h2>
              <Button variant="outline" size="sm" className="flex items-center" onClick={() => navigate("/governance")}>
                <FileText className="mr-2 h-4 w-4" /> Governance Reports
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center">
                    <AlertTriangle className="mr-2 h-5 w-5 text-amber-500" />
                    Stage Approval Gates
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {(currentProject.stages || []).slice(0, 4).map((stage) => {
                      const approvals = stage.approvals || [];
                      const approvedCount = approvals.filter(a => a.status === 'approved').length;
                      const pendingCount = approvals.filter(a => a.status === 'pending').length;
                      const rejectedCount = approvals.filter(a => a.status === 'rejected').length;
                      
                      const isComplete = stage.status === 'completed';
                      const isCurrentStage = stage.id === currentProject.currentStage;
                      
                      return (
                        <div 
                          key={stage.id} 
                          className={`border rounded-md p-3 ${isCurrentStage ? 'border-blue-300 bg-blue-50' : ''}`}
                        >
                          <div className="flex justify-between items-center">
                            <h3 className="font-medium flex items-center">
                              <span>Stage {stage.id}: {stage.name}</span>
                              {isComplete && <FileCheck className="h-4 w-4 ml-2 text-green-500" />}
                            </h3>
                            <div className={`px-2 py-1 text-xs rounded-full ${
                              isComplete ? 'bg-green-100 text-green-800' : 
                              isCurrentStage ? 'bg-blue-100 text-blue-800' : 
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {isComplete ? 'Approved' : isCurrentStage ? 'In Progress' : 'Pending'}
                            </div>
                          </div>
                          
                          {approvals.length > 0 ? (
                            <div className="mt-2 flex flex-col space-y-2">
                              <div className="flex justify-between text-xs text-muted-foreground">
                                <span>{approvals.length} approvals required</span>
                                <span>
                                  {approvedCount} approved • {pendingCount} pending • {rejectedCount} rejected
                                </span>
                              </div>
                              <div className="w-full bg-gray-100 rounded-full h-1.5">
                                <div 
                                  className="bg-green-500 h-1.5 rounded-full" 
                                  style={{ width: `${(approvedCount / approvals.length) * 100}%` }}
                                ></div>
                              </div>
                            </div>
                          ) : (
                            <div className="mt-2 text-sm text-muted-foreground">No approval gates configured</div>
                          )}
                        </div>
                      );
                    })}
                    
                    {(currentProject.stages || []).length > 4 && (
                      <Button variant="ghost" size="sm" className="w-full" onClick={() => navigate("/governance")}>
                        Show All Stages
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center">
                    <Building className="mr-2 h-5 w-5" />
                    Procurement Compliance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium">Public Contract Regulations</div>
                      <div className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Compliant</div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium">Tender Documentation</div>
                      <div className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-800">In Progress</div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium">Timeline Compliance</div>
                      <div className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Compliant</div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium">Evaluation Criteria</div>
                      <div className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">Not Started</div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium">Contract Award Notice</div>
                      <div className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">Not Started</div>
                    </div>
                    
                    <div className="mt-4">
                      <Button variant="outline" size="sm" className="w-full flex items-center" onClick={() => navigate("/procurement")}>
                        <FolderPlus className="mr-2 h-4 w-4" />
                        Manage Procurement Documents
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="md:col-span-2">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center">
                    <Clock3 className="mr-2 h-5 w-5" />
                    Recent Audit Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-2 border-l-2 border-blue-500">
                      <div className="text-sm">
                        <p className="font-medium">Stage 1 Information Exchange Updated</p>
                        <p className="text-xs text-muted-foreground">Project Brief uploaded by Jane Smith</p>
                      </div>
                      <div className="ml-auto text-xs text-muted-foreground">
                        Today, 10:23 AM
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-2 border-l-2 border-green-500">
                      <div className="text-sm">
                        <p className="font-medium">Stage 0 Approved</p>
                        <p className="text-xs text-muted-foreground">Strategic Definition approval by Client Director</p>
                      </div>
                      <div className="ml-auto text-xs text-muted-foreground">
                        Yesterday, 3:45 PM
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-2 border-l-2 border-amber-500">
                      <div className="text-sm">
                        <p className="font-medium">Risk Assessment Updated</p>
                        <p className="text-xs text-muted-foreground">Supply chain risk mitigated by Procurement Manager</p>
                      </div>
                      <div className="ml-auto text-xs text-muted-foreground">
                        01/05/2025, 11:32 AM
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-2 border-l-2 border-blue-500">
                      <div className="text-sm">
                        <p className="font-medium">Project Team Member Added</p>
                        <p className="text-xs text-muted-foreground">Robert Johnson (Structural Engineer) added by Project Manager</p>
                      </div>
                      <div className="ml-auto text-xs text-muted-foreground">
                        29/04/2025, 2:15 PM
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-2 border-l-2 border-blue-500">
                      <div className="text-sm">
                        <p className="font-medium">Budget Updated</p>
                        <p className="text-xs text-muted-foreground">Budget increased by £50,000 for additional scope</p>
                      </div>
                      <div className="ml-auto text-xs text-muted-foreground">
                        28/04/2025, 9:45 AM
                      </div>
                    </div>
                  </div>
                  
                  <Button variant="ghost" size="sm" className="w-full mt-4" onClick={() => navigate("/audit-log")}>
                    View Full Audit Log
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
